/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { useNotification } from '@ancert/aui-notification-context';
import AUIIcon from '@ancert/aui-icon';
import { toBase64 } from '@ancert/aui-utils';
import { format } from 'date-fns';
import { arrayOf, func, number, shape, string } from 'prop-types';
import AUIButton from '@ancert/aui-button';
import { useSession } from '../../../context/session.context';
import DialogBox from '../../../components/DialogBox';
import ModalNewComentario from '../../../components/ModalNewComentario';
import ModalAdjuntarArchivos from '../../../components/ModalAdjuntarArchivos';
import { FORMAT_DATE, PERMISO_MENSAJES, SIN_PERMISO_ESCRIBIR_MENSAJES } from '../../../constants';
import { postMensajesDenuncia } from '../../../services/denuncias.service';
import getDownload from '../../../services/download.service';
import { getFileIcon } from '../../../utils/fileUtils';
import { ROL } from '../../../lib/role.enum';
import getBase64FromUrl from '../../../utils/base64Utils';
import { downloadPdfBase64File, downloadPngBase64File, downloadJpgBase64File } from '../../../utils/downloadUtils';
import { ESTADO_DENUNCIA } from '../../../lib/denuncia.enum';

const Mensajes = ({ complaintDate, data, reload, complaintId, compaintStatus }) => {
  const { openNotification } = useNotification();
  const { getUser } = useSession();
  const { role } = getUser();
  const [showNewComentario, setShowNewComentario] = useState(false);
  const [showNewArchivo, setShowNewArchivo] = useState(false);
  const [havePermission, setHavePermission] = useState(true);

  useEffect(() => {
    const permisoEstado = !SIN_PERMISO_ESCRIBIR_MENSAJES.some((status) => status === compaintStatus);
    const permisoRol = PERMISO_MENSAJES.some((rol) => rol === role);
    setHavePermission(permisoEstado && permisoRol);
  }, [compaintStatus]);

  const download = async ({ blob, fileName }) => {
    const urlDownLoad = URL.createObjectURL(blob);
    const doc = await getBase64FromUrl(urlDownLoad);
    if (fileName.endsWith('.pdf')) {
      downloadPdfBase64File({ base64String: doc, fileName });
    } else if (fileName.endsWith('.png')) {
      downloadPngBase64File({ base64String: doc, fileName });
    } else {
      downloadJpgBase64File({ base64String: doc, fileName });
    }
  };

  const downloadFile = async ({ uuid, name }) => {
    const { status, error, data: result } = await getDownload(uuid);
    if (status !== 200) {
      const { message } = error;
      openNotification({
        title: 'Error',
        content: <div>{message || 'Ha ocurrido un error'}</div>,
        type: 'error',
        centerButtons: true,
      });
    } else {
      download({ blob: result, fileName: name });
    }
  };
  const renderMensajes = () =>
    data
      ?.sort((a, b) => {
        const dateA = new Date(a.creationDate).getTime();
        const dateB = new Date(b.creationDate).getTime();

        return dateA - dateB;
      })
      .map(({ creationDate, message, responseRequired, user, attachment }, i) => (
        <div
          key={`${creationDate}-${i}`}
          className={!user.role || user.role === ROL.DENUNCIANTE ? '' : 'c-sii-mensajes__incoming'}
        >
          <DialogBox
            date={format(new Date(creationDate), FORMAT_DATE.FRONT)}
            isSend={!user.role || user.role === ROL.DENUNCIANTE}
            time={`${format(new Date(creationDate), FORMAT_DATE.HOURS_MINUTES)}h`}
            title={user.name ? `${user.name} ${user.lastname1} ${user.lastname2}` : 'Anónimo'}
            isRequired={responseRequired}
            status={data && i + 1 === data.length && data[0]?.complaint?.status}
          >
            {!attachment ? (
              <p>{message}</p>
            ) : (
              <button type="button" onClick={() => downloadFile(attachment)} className="c-sii-mensajes__download">
                <AUIIcon icon={getFileIcon(attachment.name)} />
                <p>{attachment.name}</p>
              </button>
            )}
          </DialogBox>
        </div>
      ));

  const onNewComentario = async (content) => {
    let body = {};
    if (content.file) {
      const base64 = await toBase64(content.file.rawFile);
      body = {
        message: content.description,
        attachment: {
          name: content.file.rawFile.name,
          description: content.description,
          size: content.file.rawFile.size,
          data: base64.split(',')[1],
        },
      };
    } else {
      body = { message: content.description, responseRequired: content.responseRequired };
    }

    const { status, error } = await postMensajesDenuncia({ id: complaintId, data: body });
    setShowNewArchivo(false);
    setShowNewComentario(false);
    if (status === 200) {
      reload();
      return;
    }
    const { message } = error;
    openNotification({
      title: 'Error',
      content: <div>{message || 'Ha ocurrido un error'}</div>,
      type: 'error',
      centerButtons: true,
    });
  };
  const isNewMessageDisabled = compaintStatus === ESTADO_DENUNCIA.ABIERTA && role !== ROL.DENUNCIANTE;

  return (
    <>
      <div className="c-sii-mensajes">
        {havePermission && (
          <div className="c-sii-mensajes__buttons">
            <AUIButton
              className={isNewMessageDisabled && 'c-sii-mensajes__btn--disabled'}
              icon="chatBubbleOutline"
              mode="secondary"
              tooltip={
                isNewMessageDisabled
                  ? 'Para interactuar con el denunciante, primero debe ir a la pestaña Gestión de denuncia y seleccionar la opción Procesar.'
                  : 'Agregar comentario'
              }
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => {
                if (!isNewMessageDisabled) setShowNewComentario(true);
              }}
            />
            <AUIButton
              className={isNewMessageDisabled && 'c-sii-mensajes__btn--disabled'}
              icon="attach"
              mode="secondary"
              tooltip={
                isNewMessageDisabled
                  ? 'Para interactuar con el denunciante, primero debe ir a la pestaña Gestión de denuncia y seleccionar la opción Procesar.'
                  : 'Agregar archivo'
              }
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => {
                if (!isNewMessageDisabled) setShowNewArchivo(true);
              }}
            />
          </div>
        )}
        <DialogBox
          date={complaintDate ? format(new Date(complaintDate), FORMAT_DATE.FRONT) : ''}
          time={complaintDate ? `${format(new Date(complaintDate), FORMAT_DATE.HOURS_MINUTES)}h` : ''}
          title="Estado inicial"
          isSend
        >
          <div className="c-sii-mensajes__initial-state">
            <AUIIcon icon="clockOutline" color="blue" />
            Pendiente
          </div>
        </DialogBox>
        {renderMensajes()}
      </div>
      <ModalNewComentario
        show={showNewComentario}
        onAccept={onNewComentario}
        onClose={() => setShowNewComentario(false)}
      />
      <ModalAdjuntarArchivos
        show={showNewArchivo}
        onAccept={onNewComentario}
        onClose={() => setShowNewArchivo(false)}
      />
    </>
  );
};
Mensajes.propTypes = {
  complaintDate: string,
  data: arrayOf(
    shape({
      creationDate: string,
      message: string,
      complaint: shape({
        status: string,
      }),
    })
  ),
  reload: func.isRequired,
  complaintId: number.isRequired,
  compaintStatus: string,
};
Mensajes.defaultProps = {
  complaintDate: '',
  data: [],
  compaintStatus: '',
};

export default Mensajes;
